/**
 * LandlordResources — Pocketpills-style knowledge hub for Canadian
 * landlords. Route: /resources (landlord-protected).
 *
 * 2026-02 rebuild: pill-shaped hero card, rounded pastel section cards
 * with colored icon tiles, arrow-right chevrons on each item, and a
 * long dark pill CTA at the bottom. Keeps emerald palette.
 */
import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  FileText, Scale, Calculator, Wrench, Users, BookOpen, ExternalLink,
  ArrowRight, MapPin, Home,
} from "lucide-react";
import BackToPortalButton from "@/components/BackToPortalButton";

function useSEO({ title, desc, url, ldType, extraLd }) {
  useEffect(() => {
    document.title = title;
    const set = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) { el = document.createElement(selector.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    set('meta[name="description"]',        { name: "description", content: desc });
    set('link[rel="canonical"]',           { rel: "canonical", href: url });
    set('meta[property="og:type"]',        { property: "og:type", content: "website" });
    set('meta[property="og:title"]',       { property: "og:title", content: title });
    set('meta[property="og:description"]', { property: "og:description", content: desc });
    set('meta[property="og:url"]',         { property: "og:url", content: url });
    set('meta[name="twitter:card"]',       { name: "twitter:card", content: "summary_large_image" });
    let ld = document.head.querySelector(`script[data-seo="${ldType}-ld"]`);
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-seo", `${ldType}-ld`);
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": title,
      "description": desc,
      "url": url,
      ...(extraLd || {}),
    });
  }, [title, desc, url, ldType, extraLd]);
}

const SECTIONS = [
  { title: "Province RTA rules", icon: Scale, tone: "bg-emerald-600 text-white", items: [
    { label: "Ontario — RTA + N-forms (LTB)", href: "https://tribunalsontario.ca/ltb/", external: true },
    { label: "British Columbia — RTB", href: "https://www2.gov.bc.ca/gov/content/housing-tenancy/residential-tenancies", external: true },
    { label: "Quebec — TAL", href: "https://www.tal.gouv.qc.ca/en", external: true },
    { label: "Alberta — RTDRS", href: "https://www.alberta.ca/residential-tenancies", external: true },
    { label: "Province guide hub on Central Rentals", to: "/property-management/ontario" },
  ]},
  { title: "Tax + CRA filings", icon: Calculator, tone: "bg-emerald-800 text-white", items: [
    { label: "T776 Statement of Real Estate Rentals — CRA form", href: "https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/t776.html", external: true },
    { label: "NR4 for non-resident landlords", href: "https://www.canada.ca/en/revenue-agency/services/forms-publications/forms/nr4.html", external: true },
    { label: "Capital cost allowance (Class 1 buildings)", href: "https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/sole-proprietorships-partnerships/report-business-income-expenses/capital-cost-allowance.html", external: true },
    { label: "Library — Canadian Landlord Tax Guides", to: "/library" },
  ]},
  { title: "Lease + screening", icon: FileText, tone: "bg-teal-500 text-white", items: [
    { label: "Generate a province-compliant lease (in-app)", to: "/leases" },
    { label: "Tenant screening (Equifax-backed) — in-app", to: "/screening" },
    { label: "What can/can't go in a lease (CLEO)", href: "https://www.cleo.on.ca/en/publications/leases-residential", external: true },
  ]},
  { title: "Maintenance + vendors", icon: Wrench, tone: "bg-emerald-400 text-emerald-950", items: [
    { label: "Vendor directory (filter by province + trade)", to: "/community" },
    { label: "Smart maintenance triage (Mark)", to: "/tickets" },
    { label: "RentSafeTO maintenance standards", href: "https://www.toronto.ca/community-people/housing-shelter/rental-housing-tenant-information/rental-housing-standards/", external: true },
  ]},
  { title: "Community + networking", icon: Users, tone: "bg-lime-500 text-emerald-950", items: [
    { label: "Landlord Community feed", to: "/community" },
    { label: "Landlord & Investment Property Owners Forum (LandlordBC)", href: "https://landlordbc.ca/", external: true },
    { label: "FRPO (Federation of Rental-Housing Providers of Ontario)", href: "https://www.frpo.org/", external: true },
  ]},
  { title: "Central Rentals guides", icon: BookOpen, tone: "bg-emerald-200 text-emerald-900", items: [
    { label: "Library — all province + topic guides", to: "/library" },
    { label: "Compare vs Buildium, Yardi, AppFolio", to: "/vs" },
    { label: "Pricing", to: "/pricing" },
    { label: "Investor info", to: "/investors" },
  ]},
];

function ItemLink({ it }) {
  const cls = "group flex items-center gap-3 py-3 border-b border-emerald-100/70 last:border-b-0 hover:pl-1 transition-all";
  if (it.external) {
    return (
      <a href={it.href} target="_blank" rel="noreferrer" className={cls} data-testid="lr-item-ext">
        <span className="flex-1 text-slate-800 text-[15px] leading-snug">{it.label}</span>
        <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-emerald-700 flex-shrink-0"/>
      </a>
    );
  }
  return (
    <Link to={it.to} className={cls} data-testid="lr-item-int">
      <span className="flex-1 text-slate-800 text-[15px] leading-snug font-semibold">{it.label}</span>
      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-700 group-hover:translate-x-0.5 transition-all flex-shrink-0"/>
    </Link>
  );
}

export default function LandlordResources() {
  useSEO({
    title: "Landlord Resources · Central Rentals Canada — Provincial rules, CRA forms, leases",
    desc: "Everything a Canadian landlord needs in one place — provincial RTA rules, T776/NR4/CRA filings, lease and screening templates, maintenance vendors, and Central Rentals guides.",
    url: "https://centralrentals.ca/resources",
    ldType: "landlord-resources",
    extraLd: { "audience": { "@type": "Audience", "audienceType": "Canadian landlords" } },
  });
  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="landlord-resources-page">
      {/* HERO CARD */}
      <section className="pt-6 sm:pt-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-b from-emerald-100 via-emerald-50 to-white p-6 sm:p-12">
            <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-200/60 blur-2xl"/>
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-emerald-200 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-800">
                <BookOpen className="w-3.5 h-3.5"/> Landlord library
              </div>
              <h1 className="font-display mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.02] text-slate-950">
                Everything a <span className="text-emerald-600">Canadian landlord</span><br className="hidden sm:block"/> needs — in one place.
              </h1>
              <p className="mt-5 text-lg text-slate-700 leading-relaxed max-w-2xl">
                Provincial rules, CRA forms, lease templates, vendors, community, and Central Rentals guides — deep-linked to the exact page that answers each question.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTIONS GRID */}
      <section className="mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-5">
          {SECTIONS.map(({ title, icon: Icon, tone, items }) => (
            <div key={title} data-testid={`resource-section-${title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
              className="rounded-[28px] bg-white border border-emerald-100 shadow-sm p-6">
              <div className="flex items-center gap-3">
                <span className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm ${tone}`}>
                  <Icon className="w-5 h-5"/>
                </span>
                <h2 className="font-display text-lg font-extrabold text-slate-950 leading-tight">{title}</h2>
              </div>
              <div className="mt-4">
                {items.map((it, i) => <ItemLink key={i} it={it}/>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COVERAGE / CTA */}
      <section className="mt-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="rounded-[32px] bg-slate-50 border border-slate-200 p-6 sm:p-10 text-center">
            <div className="inline-flex items-center gap-3 justify-center">
              <span className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <Home className="w-5 h-5"/>
              </span>
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-950 leading-tight text-left">
                Central Rentals supports:
              </h3>
            </div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-y-3 gap-x-6 text-slate-700 text-base text-left max-w-3xl mx-auto">
              {["Alberta (AB)","British Columbia (BC)","Manitoba (MB)","New Brunswick (NB)","Newfoundland & Labrador (NL)","Northwest Territories (NT)","Nova Scotia (NS)","Nunavut (NU)","Ontario (ON)","Prince Edward Island (PE)","Quebec (QC)","Saskatchewan (SK)","Yukon (YT)"].map((p) => (
                <div key={p} className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-600"/> {p}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DARK PILL CTA */}
      <section className="mt-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/leases" data-testid="lr-cta"
            className="group inline-flex w-full items-center justify-between gap-4 rounded-full bg-slate-950 text-white py-4 pl-7 pr-2 text-base sm:text-lg font-extrabold shadow-lg shadow-slate-900/25 transition-transform hover:-translate-y-0.5">
            <span>Build a compliant lease in 10 minutes</span>
            <span className="w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center transition-transform group-hover:translate-x-0.5">
              <ArrowRight className="w-5 h-5"/>
            </span>
          </Link>
        </div>
      </section>

      <BackToPortalButton />
    </div>
  );
}
